if (!customElements.get('lookbook-icon')) {
  let documentCloseHandler = null;
  let documentCloseRefCount = 0;

  customElements.define(
    'lookbook-icon',
    class LookbookIcon extends HTMLElement {
      static closeAllActive() {
        const activeIcons = document.querySelectorAll('lookbook-icon.is-active');
        if (!activeIcons.length) {
          return;
        }

        const sliders = new Set();
        activeIcons.forEach((icon) => {
          icon.classList.remove('is-active');
          const slider = icon.closest('lookbook-card-slider');
          if (slider) {
            sliders.add(slider);
          }
        });

        sliders.forEach((slider) => {
          if (typeof slider.resetActiveIcons === 'function') {
            slider.resetActiveIcons();
          }
        });
      }
      // iOS Safari < 14 has no addEventListener on MediaQueryList. Without this
      // guard connectedCallback throws and the tap handler is never bound —
      // which is why hotspots looked completely dead on iPhone.
      static onMediaChange(mql, handler) {
        if (!mql) return;
        if (typeof mql.addEventListener === 'function') mql.addEventListener('change', handler);
        else if (typeof mql.addListener === 'function') mql.addListener(handler);
      }

      static offMediaChange(mql, handler) {
        if (!mql) return;
        if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', handler);
        else if (typeof mql.removeListener === 'function') mql.removeListener(handler);
      }

      constructor() {
        super();

        this.selectors = {
          cardContainer: '.lookbook-card',
          cardProduct: '.lookbook-card__product',
        };

        this.onInit = this.init.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
        this.lastActivateAt = 0;
        this.suppressNextClick = false;
        this.onHoverMediaChange = this.onHoverMediaChange.bind(this);
      }

      connectedCallback() {
        this.cardContainer = this.closest(this.selectors.cardContainer);
        this.cardProduct = this.querySelector(this.selectors.cardProduct);

        this.init();
        this.addEventListener('mouseover', this.onInit);

        this.hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
        LookbookIcon.onMediaChange(this.hoverMedia, this.onHoverMediaChange);

        // Small screens must always get the click/tap interaction, even when the
        // browser reports hover support (Chrome device-toolbar, hybrid laptops,
        // touch screens with a mouse attached). Without this the hotspot content
        // is unreachable on mobile.
        this.smallScreenMedia = window.matchMedia('(max-width: 767px)');
        LookbookIcon.onMediaChange(this.smallScreenMedia, this.onHoverMediaChange);

        this.setupTouchInteraction();
      }

      disconnectedCallback() {
        this.removeEventListener('mouseover', this.onInit);
        this.teardownTouchInteraction();

        LookbookIcon.offMediaChange(this.hoverMedia, this.onHoverMediaChange);
        LookbookIcon.offMediaChange(this.smallScreenMedia, this.onHoverMediaChange);
      }

      onHoverMediaChange() {
        this.teardownTouchInteraction();
        this.setupTouchInteraction();
      }

      needsClickInteraction() {
        const isSmallScreen = this.smallScreenMedia ? this.smallScreenMedia.matches : window.innerWidth <= 767;
        return isSmallScreen || !this.hoverMedia.matches;
      }

      setupTouchInteraction() {
        if (!this.needsClickInteraction() || this.touchInteractionEnabled) {
          return;
        }

        this.touchInteractionEnabled = true;
        this.addEventListener('click', this.onClick);
        // iOS Safari dispatches a synthetic click ~300ms after touchend. Acting
        // on touchend AND swallowing the follow-up click stops the card from
        // opening and immediately closing again (looked like "nothing happens").
        this.addEventListener('touchend', this.onTouchEnd, { passive: false });
        this.setupDocumentClose();
      }

      teardownTouchInteraction() {
        if (!this.touchInteractionEnabled) {
          return;
        }

        this.touchInteractionEnabled = false;
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('touchend', this.onTouchEnd);
        this.teardownDocumentClose();
      }

      setupDocumentClose() {
        if (documentCloseHandler) {
          documentCloseRefCount++;
          return;
        }

        documentCloseHandler = (event) => {
          const target = event.target;
          if (!target || typeof target.closest !== 'function') return;

          if (target.closest('lookbook-icon') || target.closest('.lookbook-card__product')) {
            return;
          }

          // Grace period: iOS fires a synthetic click right after the tap that
          // opened the card, which would close it again instantly.
          const active = document.querySelector('lookbook-icon.is-active');
          if (active && Date.now() - (active.lastActivateAt || 0) < 500) {
            return;
          }

          LookbookIcon.closeAllActive();
        };

        // iOS Safari only bubbles clicks from non-interactive elements when the
        // document is "clickable" — listening on touchend as well guarantees the
        // outside-tap close works there.
        document.addEventListener('click', documentCloseHandler);
        document.addEventListener('touchend', documentCloseHandler, { passive: true });
        documentCloseRefCount = 1;
      }

      teardownDocumentClose() {
        if (!documentCloseHandler || documentCloseRefCount === 0) {
          return;
        }

        documentCloseRefCount--;

        if (documentCloseRefCount === 0) {
          document.removeEventListener('click', documentCloseHandler);
          document.removeEventListener('touchend', documentCloseHandler);
          documentCloseHandler = null;
        }
      }

      onTouchEnd(event) {
        if (event.target.closest(this.selectors.cardProduct)) {
          return;
        }

        // Ignore multi-touch / swipes.
        if (event.changedTouches && event.changedTouches.length > 1) return;

        event.preventDefault();
        event.stopPropagation();

        // Flag-based (not time-based): the very next click is the OS-generated
        // ghost click for this tap, whenever it arrives. Swallow exactly one.
        this.suppressNextClick = true;
        if (this.suppressTimer) clearTimeout(this.suppressTimer);
        this.suppressTimer = setTimeout(() => {
          this.suppressNextClick = false;
        }, 1500);

        this.toggle();
      }

      onClick(event) {
        if (event.target.closest(this.selectors.cardProduct)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        // Swallow the synthetic click that iOS fires after touchend.
        if (this.suppressNextClick) {
          this.suppressNextClick = false;
          if (this.suppressTimer) clearTimeout(this.suppressTimer);
          return;
        }

        this.toggle();
      }

      toggle() {
        this.lastActivateAt = Date.now();

        const wasActive = this.classList.contains('is-active');
        this.deactivateSiblings();

        if (wasActive) {
          this.deactivate();
          return;
        }

        this.classList.add('is-active');
        this.init();
        this.dispatchActivate();
      }

      deactivateSiblings() {
        if (!this.cardContainer) {
          return;
        }

        this.cardContainer.querySelectorAll('lookbook-icon.is-active').forEach((icon) => {
          if (icon !== this) {
            icon.classList.remove('is-active');
          }
        });
      }

      deactivate() {
        this.classList.remove('is-active');
        this.notifySliderReset();
      }

      dispatchActivate() {
        const index = Number(this.dataset.index);

        this.dispatchEvent(
          new CustomEvent('lookbook-icon:activate', {
            bubbles: true,
            detail: { index: Number.isNaN(index) ? null : index },
          })
        );
      }

      notifySliderReset() {
        const slider = this.closest('lookbook-card-slider');
        if (slider && typeof slider.resetActiveIcons === 'function') {
          slider.resetActiveIcons();
        }
      }

      init() {
        if (!this.cardProduct || !this.cardContainer) {
          return;
        }

        this.cardContainerOffsetX = this.cardContainer.getBoundingClientRect().left;
        this.cardContainerOffsetY = this.cardContainer.getBoundingClientRect().top;

        this.offsetX = this.getBoundingClientRect().left - this.cardContainerOffsetX;
        this.offsetY = this.getBoundingClientRect().top - this.cardContainerOffsetY;

        this.cardProductWidth = this.cardProduct.clientWidth;
        this.cardProductHeight = this.cardProduct.clientHeight;
        this.cardContainerWidth = this.cardContainer.clientWidth;

        if (this.offsetX > this.cardProductWidth) {
          this.cardProduct.style.left = 'auto';
          if (this.cardContainerWidth - this.offsetY < 15) {
            this.cardProduct.style.right = 'calc(-100% + 2rem)';
          } else {
            this.cardProduct.style.right = '-100%';
          }
        } else {
          this.cardProduct.style.right = 'auto';
          if (this.cardContainerWidth - this.offsetX > this.cardProductWidth) {
            if (this.offsetX < 15) {
              this.cardProduct.style.left = 'calc(-100% + 2rem)';
            } else {
              this.cardProduct.style.left = '-100%';
            }
          } else {
            this.cardProduct.style.left = this.cardContainerWidth - this.cardProductWidth - this.offsetX + 'px';
          }
        }

        if (this.offsetY > this.cardProductHeight) {
          this.cardProduct.style.top = 'auto';
          this.cardProduct.style.bottom = '100%';
        } else {
          this.cardProduct.style.bottom = 'auto';
          this.cardProduct.style.top = '100%';
        }

        // The logic above only keeps the card inside the IMAGE container. On
        // narrow screens a hotspot near an edge still lands outside the
        // viewport (and gets clipped), so the text never appears. Clamp it.
        this.clampToViewport();
      }

      clampToViewport() {
        if (!this.cardProduct) return;

        const margin = 12;
        const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
        const rect = this.cardProduct.getBoundingClientRect();
        if (!rect.width) return;

        let delta = 0;
        if (rect.left < margin) {
          delta = margin - rect.left;
        } else if (rect.right > viewportWidth - margin) {
          delta = viewportWidth - margin - rect.right;
        }

        if (!delta) return;

        const computed = window.getComputedStyle(this.cardProduct);
        const currentLeft = parseFloat(computed.left);

        if (Number.isFinite(currentLeft)) {
          this.cardProduct.style.right = 'auto';
          this.cardProduct.style.left = currentLeft + delta + 'px';
        } else {
          const currentRight = parseFloat(computed.right);
          if (Number.isFinite(currentRight)) {
            this.cardProduct.style.left = 'auto';
            this.cardProduct.style.right = currentRight - delta + 'px';
          }
        }

        // Also clamp vertically so a hotspot low in the image doesn't push the
        // card below the fold on mobile.
        const after = this.cardProduct.getBoundingClientRect();
        const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
        if (after.bottom > viewportHeight - margin && this.offsetY > after.height) {
          this.cardProduct.style.top = 'auto';
          this.cardProduct.style.bottom = '100%';
        }
      }
    }
  );
}